// skuGrammar.ts — the one place a SKU is built or judged on the frontend.
//
// It used to be four constants and a function buried in the middle of
// PurchaseOrderForm.tsx, which was fine while that form was the only place a
// SKU was ever born. Scan-to-stock is the second, so the grammar moves here
// rather than being copied. The form's behavior is unchanged: the board pieces
// below are the same code, in a new file.
//
// ── TWO GRAMMARS, ON PURPOSE ────────────────────────────────────────────────
// BOARDS are strict: {BRAND}-{BOARD}-{GEN}-{REV}[-{REV2}][-V{N}]-{VENDOR}[-P],
// because every segment carries a decision somebody made (a re-spin, a second
// assembler, a stuffing variant) and the backend parses it to resolve a family.
//
// WHICH BOARD CODES EXIST IS NO LONGER A CONSTANT HERE. It is the `item_kinds`
// table, admin-editable like the vendor taxonomy, read through
// stores/useItemKindsStore. A kind whose code_mode is `structured` is a board
// segment; a `short` one is the stem of a non-board code. The GRAMMAR stays
// here, the VOCABULARY moved to the database, and the server is the authority
// on both.
//
// EVERYTHING ELSE is loose, per docs/planning/sku-naming-convention.md: "unique,
// recognizable, prefixed with FF". Non-board items share no metadata shape, and
// nothing in the app parses those strings. The live catalog settled on
// {BRAND}-{TYPE}-{GEN}-{N} anyway (FF-CAB-02-1, FF-ANT-02-1, FF-MTR-02-1), so
// that is the shape suggestItemSku proposes.
//
// ── A SUGGESTION IS NOT AN ANSWER ───────────────────────────────────────────
// suggestItemSku fills a field a person then reads and can retype. It is
// allowed to be wrong in the way a good default is wrong: obvious at a glance
// and one keystroke from right. It must never be EMPTY, because an empty
// required field on a rapid-entry screen is the thing that stops the flow.

import type { CommodityClass } from '../Receiving/types';
import { INSTANCE, type Instance } from '../instance';

// Brand prefixes mirror api/src/parts/parts-catalog-service.js's BRAND_PREFIXES.
// FF = Firefly Drone Shows, SD = Swarm Defense. Adding a third requires touching
// the backend regex first.
export const BRAND_PREFIXES = ['FF', 'SD'] as const;
export type Brand = (typeof BRAND_PREFIXES)[number];

// WHICH BRAND THIS DEPLOYMENT MINTS UNDER.
//
// This used to be a literal 'FF' default parameter on suggestItemSku, which is
// how SwarmSystems ended up handing out FF- SKUs: every caller that did not
// think to pass a brand got Firefly's. A default that is only correct on one of
// two instances is not a default, it is a bug with a fallback's manners.
//
// Derived from the build-time instance rather than branched on at each call
// site, per the "instance config, not `if (isSwarm)`" rule in web/CLAUDE.md.
// The one caller that legitimately overrides it is QuoteDraftPage, which reads
// the instance off the PARSE RESPONSE — server truth about which API answered,
// which is strictly better than a build-time guess when the two could disagree.
// A PURE FUNCTION plus a bound constant, not a bare ternary. api/CLAUDE.md
// spells the rule for the backend ("use a pure xFor(instance) helper, never
// delete require.cache to test it") and the reasoning is identical here: a
// module-load ternary can only be exercised by busting Jest's module registry,
// which is the frontend spelling of the thing that rule bans. brandFor is
// testable in one line with no resetModules.
export function brandFor(instance: Instance): Brand {
  return instance === 'swarm' ? 'SD' : 'FF';
}

export const DEFAULT_BRAND: Brand = brandFor(INSTANCE);

// Mirrors the strict board regex in api/src/parts/parts-catalog-service.js,
// capture groups included, so one string can be both judged and taken apart.
//
// Groups: 1=BRAND, 2=BOARD, 3=GEN, 4=REV1, 5=REV2 (optional), 6=VARIANT number
// (without its V), 7=VENDOR, 8=the literal -P.
export const BOARD_SKU_REGEX =
  /^(FF|SD)-([A-Z]{2,3})-(\d{2})-(\d+)(?:-(\d+))?(?:-V(\d+))?-([A-Z]{2})(-P)?$/;
export const BRAND_PREFIX_PATTERN = /^(FF|SD)-/;

// parts_catalog.sku is varchar(50); the backend rejects anything longer with
// invalid_sku_format. Checking here turns that into a note under the field.
export const SKU_MAX_LENGTH = 50;

export interface BoardFields {
  brand: Brand;           // 'FF' or 'SD' — see BRAND_PREFIXES
  board: string;          // BOARD segment (e.g. 'AP')
  generation: string;     // 2 digits (e.g. '02')
  revMajor: string;       // positive integer
  revMinor: string;       // optional positive integer
  variant: string;        // optional integer (V{N})
  prototype: boolean;
  vendorCode: string;     // 2-letter MANUFACTURER code baked into the SKU
}

export function previewBoardSku(b: BoardFields): string {
  const segs = [b.brand, b.board, b.generation, b.revMajor];
  if (b.revMinor) segs.push(b.revMinor);
  let sku = segs.join('-');
  if (b.variant) sku += `-V${b.variant}`;
  if (b.vendorCode) sku += `-${b.vendorCode}`;
  if (b.prototype) sku += '-P';
  return sku;
}

/**
 * The segments behind a board SKU, or null when the string is not one.
 *
 * The inverse of previewBoardSku, and the frontend half of the backend's
 * `parseBoardSku`. It exists because a document lane can arrive holding a
 * board-shaped string the AI proposed: the SKU builder edits SEGMENTS, so a
 * guess that cannot be taken apart cannot be shown in the control that owns it.
 *
 * Round-trips: previewBoardSku(parseBoardSku(s)!) === s for anything this
 * accepts.
 */
export function parseBoardSku(sku: string | null | undefined): BoardFields | null {
  const match = BOARD_SKU_REGEX.exec(String(sku || '').trim().toUpperCase());
  if (!match) return null;
  const [, brand, board, generation, revMajor, revMinor, variant, vendorCode, prototype] = match;
  return {
    brand: brand as Brand,
    board,
    generation,
    revMajor,
    revMinor: revMinor || '',
    variant: variant || '',
    prototype: !!prototype,
    vendorCode,
  };
}

// ---------------------------------------------------------------------------
// Reading a purchase-order line
// ---------------------------------------------------------------------------

/**
 * ONE table, two jobs: the SKU's type token and the commodity-class suggestion.
 *
 * That is deliberate. "Screw" implies both `FF-SCR-…` and hardware_fastener, and
 * keeping them in separate tables is how the two answers drift into disagreeing
 * about the same word. First match wins, so the list is ordered most-specific
 * first ("dust cover" before "cover").
 *
 * Every entry has to be defensible from the word alone. Anything that needs to
 * know what the thing IS — is this motor a spare or a build part? — is left out,
 * and the person picks. A wrong preselection is worse than an empty one: it is a
 * wrong answer wearing the look of a reviewed one.
 */
const LINE_KEYWORDS: { match: RegExp; token: string; commodityClass: CommodityClass }[] = [
  // Fasteners and mounting hardware
  { match: /\bscrews?\b|\bbolts?\b/i, token: 'SCR', commodityClass: 'hardware_fastener' },
  { match: /\bstand-?offs?\b/i, token: 'STD', commodityClass: 'hardware_fastener' },
  { match: /\bgrommets?\b/i, token: 'GMT', commodityClass: 'hardware_fastener' },
  { match: /\bwashers?\b/i, token: 'WSH', commodityClass: 'hardware_fastener' },
  { match: /\bnuts?\b/i, token: 'NUT', commodityClass: 'hardware_fastener' },
  { match: /\brivets?\b|\bfasteners?\b/i, token: 'HW', commodityClass: 'hardware_fastener' },
  // Wire assemblies
  { match: /\bharness(es)?\b|\bpigtails?\b|\bcables?\b/i, token: 'CAB', commodityClass: 'cable_harness' },
  // Single electrical parts
  { match: /\bantennas?\b/i, token: 'ANT', commodityClass: 'electronic_component' },
  { match: /\bconnectors?\b|\bheaders?\b/i, token: 'CON', commodityClass: 'electronic_component' },
  // Things that move or switch
  { match: /\bmotors?\b/i, token: 'MTR', commodityClass: 'electromechanical' },
  { match: /\bbuttons?\b|\bswitch(es)?\b/i, token: 'BTN', commodityClass: 'electromechanical' },
  { match: /\bfans?\b/i, token: 'FAN', commodityClass: 'electromechanical' },
  // Cells and packs, which ship and store under their own rules
  { match: /\bbatter(y|ies)\b|\blipos?\b|\bcells?\b/i, token: 'BATT', commodityClass: 'battery' },
  // Formed, machined, moulded or printed
  { match: /\bdust\s?covers?\b/i, token: 'DST', commodityClass: 'mechanical' },
  { match: /\bdomes?\b/i, token: 'DOME', commodityClass: 'mechanical' },
  { match: /\bskirts?\b/i, token: 'SKRT', commodityClass: 'mechanical' },
  { match: /\bframes?\b|\bbrackets?\b|\bhousings?\b|\bcovers?\b/i, token: 'FRM', commodityClass: 'mechanical' },
  { match: /\bprops?\b|\bpropellers?\b/i, token: 'PROP', commodityClass: 'mechanical' },
  // Stock used by length or weight
  { match: /\bfilaments?\b/i, token: 'FIL', commodityClass: 'material' },
  { match: /\badhesives?\b|\bepox(y|ies)\b|\bglues?\b/i, token: 'ADH', commodityClass: 'material' },
  { match: /\bsolder\b|\bflux\b/i, token: 'SLD', commodityClass: 'material' },
  { match: /\bwires?\b|\bspools?\b/i, token: 'WIR', commodityClass: 'material' },
  // Boxes, foam, labels, bags
  { match: /\blabels?\b|\bfoam\b|\bbags?\b|\bpackaging\b/i, token: 'PKG', commodityClass: 'packaging' },
  // Bare boards. Assembled ones use the board grammar above, not this path.
  { match: /\bbare\s?boards?\b|\bpcbs?\b/i, token: 'PCB', commodityClass: 'pcb' },
];

/**
 * The commodity class a line's own words justify, or '' for "nobody knows yet".
 *
 * Nothing here ever suggests a material_type: every line on a purchase order is
 * bought, so raw_material is the SAP-honest answer for all of them and the scan
 * screen preselects it directly.
 */
export function suggestCommodityClass(description: string | null | undefined): CommodityClass | '' {
  const text = String(description || '');
  if (!text.trim()) return '';
  const hit = LINE_KEYWORDS.find((k) => k.match.test(text));
  return hit ? hit.commodityClass : '';
}

/**
 * The 2-4 character type token for the SKU's second segment.
 *
 * Exported because it is also the best guess at an item KIND: the scan and
 * quote lanes look this token up in `item_kinds` and, when it is a live kind,
 * the line arrives already knowing what sort of thing it is. Same word, two
 * jobs, one table, for the same reason LINE_KEYWORDS carries both answers.
 */
export function suggestTypeToken(description: string | null | undefined): string {
  const text = String(description || '');
  const hit = LINE_KEYWORDS.find((k) => k.match.test(text));
  if (hit) return hit.token;
  // No keyword matched, so borrow the line's own first word rather than
  // inventing a bucket. "Ferrule, 18AWG" proposes FF-FERR-02-1, which a person
  // reads as obviously theirs to correct. ITEM is the last resort.
  const firstWord = (text.match(/[A-Za-z][A-Za-z0-9]{1,}/) || [])[0];
  return firstWord ? firstWord.slice(0, 4).toUpperCase() : 'ITEM';
}

// Every non-board SKU in the live catalog carries the hardware generation in
// this slot (FF-CAB-02-1, FF-SCR-ESC-02-1). It is part of the shape, not a
// claim about the item, and the field stays editable either way.
export const CURRENT_GENERATION = '02';

export interface SkuSuggestionInput {
  description: string | null | undefined;
  /** Defaults to DEFAULT_BRAND, which follows the deployment. Pass explicitly
   *  only when you know better than the build does, e.g. from a server response. */
  brand?: Brand;
  /** Highest numeric tail already taken per stem, from the scan-parse response. */
  tailMax?: Record<string, number>;
  /** SKUs proposed on OTHER lines of the same document, so two boxes of screws
   *  on one purchase order do not both propose the same string. */
  alsoTaken?: Iterable<string>;
}

/**
 * Propose a SKU for an item nobody has cataloged yet.
 *
 * Deterministic: the same line, the same catalog and the same document always
 * produce the same string. It walks up from the highest tail the server reported
 * for this stem, then past anything the rest of this document already claimed.
 */
export function suggestItemSku({
  description,
  brand = DEFAULT_BRAND,
  tailMax = {},
  alsoTaken = [],
}: SkuSuggestionInput): string {
  const stem = `${brand}-${suggestTypeToken(description)}-${CURRENT_GENERATION}`;
  const taken = new Set<string>();
  for (const sku of alsoTaken) taken.add(String(sku).toUpperCase());

  let n = (tailMax[stem] || 0) + 1;
  // Bounded so a pathological document cannot spin: 999 of one stem on one
  // purchase order is not a thing, and the field is editable regardless.
  while (taken.has(`${stem}-${n}`) && n < 1000) n += 1;
  return `${stem}-${n}`;
}

/**
 * One line of what a code of a given kind looks like.
 *
 * The board list used to be a constant in this file, so "which kinds exist" was
 * a code question. It is a table now (item_kinds), and a kind carries a code
 * MODE: `structured` kinds are segments in the strict board grammar above,
 * `short` kinds are the stem of a short code. Two grammars is one word of
 * explanation and one example, and the example is the half that lands.
 *
 * The brand follows the deployment, so Swarm's examples read SD-.
 */
export function exampleCode(key: string, codeMode: 'structured' | 'short'): string {
  const stem = (key || '??').toUpperCase();
  return codeMode === 'short'
    // A fixed sample tail. Short codes are STEM-XXXX with a generated tail, so
    // any real-looking four characters say more than a row of Xs.
    ? `${stem}-7K3Q`
    : `${DEFAULT_BRAND}-${stem}-${CURRENT_GENERATION}-1-CH`;
}

/** Is this string one the backend will accept as a SKU at all? */
export function skuLooksMintable(sku: string): boolean {
  const trimmed = sku.trim();
  return BRAND_PREFIX_PATTERN.test(trimmed) && trimmed.length <= SKU_MAX_LENGTH;
}
